/* CR47 hero video slot. Never on the LCP path: created after `load`, when idle.
   Never under reduced motion or Save-Data. Muted, playsinline, pausable, pauses off screen. */
(() => {
  if (customElements.get('nm-hero-video')) return;
  // 2026-10-09 (owner): autoplay for everyone, like the video row. OS reduced motion and Save-Data no longer stop
  // it (they kept it frozen on the owner's own PC); it stays muted with a pause button (WCAG 2.2.2), and the
  // theme's Minimal motion setting still stops it.
  const reduced = () => document.documentElement.getAttribute('data-nm-motion') === 'reduced';
  const saveData = () => false;
  /* CR70: phones get the poster only. Autoplay hero video is the leading cause of LCP failure
     because the browser prioritises the video download over other critical resources, and the
     phone is where the slowest connections and the Add-to-Cart budget both live. 750px is this
     theme's own breakpoint, the one nm-sections.css uses for the hero's desktop layout. */
  const smallScreen = () => !matchMedia('(min-width: 750px)').matches;
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 200));

  class NmHeroVideo extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      this.label = this.querySelector('.nm-hero__toggle-label');
      this.userPaused = false;
      /* Evaluated once, on connect. Deliberately not re-evaluated on resize: a desktop window
         dragged narrow keeps what it started with, rather than mounting or tearing down a video
         mid-session for a case no shopper actually hits. */
      /* CR76 r4: a phone plays the video only when the section supplies a light phone file (data-src-mobile,
         setting "Play the video on phones too"); otherwise it keeps the poster, as CR70 decided. */
      if (smallScreen()) {
        if (!this.dataset.srcMobile) return;
        this.dataset.src = this.dataset.srcMobile;
      }
      if (reduced() || saveData()) {
        if (this.dataset.reduced === 'tap') this.offerPlay();
        return;
      }
      let started = false;
      const go = () => { if (started) return; started = true; idle(() => this.mount(this.dataset.autoplay === 'true')); };
      // Load event, or 1.5 s, whichever first: a slow pop-up or chat script must not hold the video back.
      if (document.readyState === 'complete') go(); else { addEventListener('load', go, { once: true }); setTimeout(go, 1500); }
    }

    offerPlay() {
      this.setLabel(true);
      this.btn.hidden = false;
      this.btn.addEventListener('click', () => { this.userPaused = false; this.mount(true); }, { once: true });
    }

    mount(autoplay) {
      if (this.video) return;
      const v = document.createElement('video');
      v.muted = true; v.defaultMuted = true; v.playsInline = true;
      v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
      v.loop = this.dataset.loop === 'true';
      v.preload = autoplay ? 'auto' : 'metadata';
      /* CR98: reuse the exact poster file the page already downloaded. The <img> above this slot has
         already fetched one size from its srcset; pointing the video at a different URL made the
         browser fetch a second copy of the same picture, which showed up in the Lighthouse trace as
         two poster downloads on the home page. currentSrc is whichever one it actually chose. */
      const shown = this.closest('.nm-hero')?.querySelector('.nm-hero__poster');
      v.poster = (shown && shown.currentSrc) || this.dataset.poster || '';
      v.className = 'nm-hero__video';
      v.setAttribute('aria-hidden', 'true');
      const s = document.createElement('source');
      s.src = this.dataset.src;
      if (this.dataset.type) s.type = this.dataset.type;
      s.addEventListener('error', () => this.fail());
      v.append(s);
      v.addEventListener('playing', () => this.classList.add('is-playing'));
      this.prepend(v);
      this.video = v;
      /* CR76 r4 (owner): the Pause button can be switched off. Tapping the video then pauses and resumes it, so there is
         still a way to stop the motion (WCAG 2.2.2). */
      if (this.dataset.pauseButton === 'false') {
        this.addEventListener('click', (e) => { if (e.target === v || e.target === this) this.toggle(); });
      } else {
        this.btn.hidden = false;
        this.btn.onclick = () => this.toggle();
      }
      if (autoplay) this.play(); else this.setLabel(true);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => {
          if (!e.isIntersecting) v.pause();
          else if (!this.userPaused) this.play();
        }, { threshold: 0.25 }).observe(this);
      }
    }

    play() {
      if (!this.video) return;
      const p = this.video.play();
      this.setLabel(false);
      // Refused (iPhone Low Power Mode, some PC settings): show Play, and start on the first touch, click, key or scroll.
      if (p && p.catch) p.catch(() => {
        this.setLabel(true);
        if (this.waiting) return;
        this.waiting = true;
        // Safari only allows play() from a completed tap (touchend/click), so listen to those and keep listening
        // until it actually plays.
        const evs = ['touchend', 'click', 'pointerup', 'keydown'];
        const retry = () => {
          if (this.userPaused || !this.video) return;
          const q = this.video.play();
          if (q && q.then) q.then(() => { evs.forEach((t) => removeEventListener(t, retry, true)); this.waiting = false; this.setLabel(false); }).catch(() => {});
        };
        evs.forEach((t) => addEventListener(t, retry, { capture: true, passive: true }));
      });
    }

    toggle() {
      if (this.video.paused) { this.userPaused = false; this.play(); }
      else { this.userPaused = true; this.video.pause(); this.setLabel(true); }
    }

    setLabel(paused) {
      this.btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.label.textContent = paused ? 'Play video' : 'Pause video';
    }

    fail() {
      if (this.video) this.video.remove();
      this.video = null;
      this.btn.hidden = true;
      this.dataset.failed = 'true';
    }
  }
  customElements.define('nm-hero-video', NmHeroVideo);
})();
